import React, { useEffect, useState } from "react";
import { Grid } from "@material-ui/core";
import _ from "lodash";
import { AxiosError } from "axios";

import withAuthority from "../../../../components/Auth/withAuthority";
import Authorities from "../../../../auth/authorities";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import { useRouteMatch } from "react-router";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import { fetchAllFilterLocations } from "../../../../services/locationApp/locationFilterService";
import {
  fetchAllStockGroups,
  fetchAllStockItemsDepartmentsInfo,
  fetchAllStockItemsGroupsInfo,
  fetchAllStockItemsProductInfo,
  fetchMapProductInfo,
  fetchStockItemInfoGetAll,
} from "../../../../services/inventory/stockItems";
import StockItemsHeader from "./StockItemsHeader";
import InventoryDetails from "../../../../components/common/InventoryDetails/InventoryDetails";
import AddStockItemsModal from "../../../../components/AddStockItemsModal";
import {
  fetchAllSelectedSuppliersInfo,
  fetchAllSuppliersInfo,
} from "../../../../services/inventory/supplier";
import Toast from "../../../../components/alerts/Toast";
import AddMapProductModal from "../../../../components/AddMapProductModal";
import { fetchAllMasterLocations } from "../../../../services/inventory/masterLocations";
import { getIsAuthorized } from "../../../../utils/authorities";
import WizardModal from "./WizardModal";
import AlertDialog from "../../../../components/alerts/AlertDialog";

interface Location {
  id: string;
  businessDisplayName: string;
}

/**
 * StockItems Component
 *
 * This React component is responsible for managing and displaying stock items within a specific location.
 * It provides features for creating, and editing stock items, as well as managing suppliers and product mappings.
 * The component is integrated with various APIs to fetch and update stock item information.
 *
 * Key Features:
 * - Fetching and displaying stock item groups, departments, and products.
 * - Sorting and filtering stock items.
 * - Creating and editing stock items.
 * - Managing suppliers and product mappings.
 * - Displaying success and error messages.
 */
const StockItems: React.FunctionComponent = (props: any) => {
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [disableButton, setDisableButton] = useState(false);
  const [mapProductSkeletonLoading, setMapProductSkeletonLoading] =
    useState(false);
  const [createdId, setCreatedId] = useState([]);
  const [locationSelectorList, setLocationSelectorList] = useState([]);
  const [stockMappingDetailsNode, setStockMappingDetailsNode] = useState([]);
  const [supplierDetailsNode, setSupplierDetailsNode] = useState([]);
  const [supplierDetailsNodeObj, setSupplierDetailsNodeObj] = useState({});
  const [supplierDetailsNodeInitial, setSupplierDetailsNodeInitial] = useState(
    [],
  );
  const [isGetLocationInfo, setIsGetLocationInfo] = useState(false);
  const [isOpenSkeletonLoading, setIsOpenSkeletonLoading] = useState(true);
  const [isOpenCreateStockItemsModal, setIsOpenCreateStockItemsModal] =
    useState(false);
  const [isOpenEditStockItemsModal, setIsOpenEditStockItemsModal] =
    useState(false);
  const [isCreated, setIsCreated] = useState(false);
  const [success, setSuccess] = useState("");
  const [menuGroups, setMenuGroups] = useState([]);
  const [menuDepartments, setMenuDepartments] = useState<any>([]);
  const [menuProducts, setMenuProducts] = useState([]);
  const [selectedNodeData, setSelectedNodeData] = useState<any>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [groupId, setGroupId] = useState("");
  const [
    stockItemGroupDepartmentNodeInSelector,
    setStockItemGroupDepartmentNodeInSelector,
  ] = useState<any>([]);
  const [
    stockItemGroupDepartmentNodeInSelectorInitial,
    setStockItemGroupDepartmentNodeInSelectorInitial,
  ] = useState<any>([]);
  const [activeGroupId, setActiveGroupId] = useState(0);
  const [isOpenMapProductsEditModal, setIsOpenMapProductsEditModal] =
    useState(false);
  const [initialSelectedSupplierList, setInitialSelectedSupplierList] =
    useState<any>([]);
  const [id, setId] = useState("");
  const [searchName, setSearchName] = useState("");
  const [searchNameTab, setSearchNameTab] = useState("");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [selectedGroupObj, setSelectedGroupObj] = useState<any>({});
  const [stockGroupSelectedNode, setStockGroupSelectedNode] = useState<any>([]);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");
  const [isLoadingToast, setIsLoadingToast] = useState(false);
  const [isSearch, setIsSearch] = useState(false);
  const [isGetApi, setIsGetApi] = useState(false);
  const [isOpenNewModal, setIsOpenNewModal] = useState(false);
  const [searchedDepartmentList, setSearchedDepartmentList] = useState<any>([]);
  const [isActiveLocationId, setIsActiveLocationId] = useState(true);
  const [isOpenWizard, setIsOpenWizard] = useState(false);

  const match: any = useRouteMatch();
  // Map to store elements by title
  const elementsByTitle: any = {};

  const handleGetMasterLocations = async () => {
    try {
      const res = await fetchAllMasterLocations(match.params.locationId);
      if (
        (res.data && res.data.data && res.data.data.active) ||
        getIsAuthorized(Authorities.STOCK_CONFIGURATION_READ)
      ) {
        getAllFilterLocation("");
        setIsActiveLocationId(true);
      } else {
        setIsActiveLocationId(false);
        setIsLoading(false);
        setIsOpenSkeletonLoading(false);
      }
    } catch (error) {
      // If an error occurs during the API call, set the 'error' state and disable loading
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsLoading(false);
      setIsOpenSkeletonLoading(false);
    }
  };

  /**
   * Effect to set the document title and fetch location information on component mount.
   */
  useEffect(() => {
    // Set the document title to "Eat Presto - Stock Item"
    document.title = "Inventory - Stock Items";

    // Fetch location information with an empty searchName
    handleGetMasterLocations();
  }, []);

  /**
   * Function to fetch stock items groups information and update the 'menuGroups' state.
   */
  const handleGetStockItemsGroupsInfo = async (
    headerName: any,
    shortingMethod: any,
  ) => {
    try {
      // Attempt to fetch stock items groups information using the 'fetchAllStockItemsGroupsInfo' API
      const res = await fetchAllStockItemsGroupsInfo(
        match.params.locationId,
        headerName,
        shortingMethod,
      );

      // Set the 'menuGroups' state with the retrieved data from the response
      setMenuGroups(res.data.data);
    } catch (error) {
      const err: any = error as AxiosError;
      setIsLoading(false);
      setIsOpenSkeletonLoading(false);
      if (err.response.status !== 404) {
        // If an error occurs during the API call, set the 'error' state with an unexpected error message
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      }
    }
  };

  /**
   * Function to fetch stock items departments information and update the 'menuDepartments' state.
   */
  const handleGetStockItemsDepartmentsInfo = async () => {
    try {
      // Attempt to fetch stock items departments information using the 'fetchAllStockItemsDepartmentsInfo' API
      const res = await fetchAllStockItemsDepartmentsInfo(
        match.params.locationId,
      );
      if (res.data && res.data.data && Object.keys(res.data.data).length > 0) {
        const newData = Object.fromEntries(
          Object.entries(res.data.data).filter(
            ([key, value]: any) =>
              value.name !== "Online Hidden" && value.name !== "Templates",
          ),
        );
        // const sortedObj = Object.fromEntries(
        //   Object.entries(newData).sort(([, a]: any, [, b]: any) => {console.log("dfsgswfadfasdasd", a, b); return a.name.localeCompare(b.name)})
        // );
        // Set the 'menuDepartments' state with the retrieved data from the response
        setMenuDepartments(newData);
      } else {
        setMenuDepartments({});
      }
    } catch (error) {
      const err: any = error as AxiosError;
      setIsLoading(false);
      setIsOpenSkeletonLoading(false);
      if (err.response.status !== 404) {
        // If an error occurs during the API call, set the 'error' state with an unexpected error message
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      }
    }
  };

  /**
   * Function to fetch stock items products information and update the 'menuProducts' state.
   */
  const handleGetStockItemsProductsInfo = async () => {
    try {
      // Attempt to fetch stock items products information using the 'fetchAllStockItemsProductInfo' API
      const res = await fetchAllStockItemsProductInfo(match.params.locationId);

      // Set the 'menuProducts' state with the retrieved data from the response
      setMenuProducts(res.data.data);
    } catch (error) {
      const err: any = error as AxiosError;
      setIsLoading(false);
      setIsOpenSkeletonLoading(false);
      if (err.response.status !== 404) {
        // If an error occurs during the API call, set the 'error' state with an unexpected error message
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      }
    }
  };

  /**
   * Function to fetch stock items groups, departments, and products information collectively.
   * Calls individual functions to retrieve and update corresponding states.
   */
  const handleGetProductDepartmentGroup = (
    headerName: any,
    shortingMethod: any,
  ) => {
    // Call function to fetch stock items groups information and update 'menuGroups' state
    handleGetStockItemsGroupsInfo(headerName, shortingMethod);

    // Call function to fetch stock items departments information and update 'menuDepartments' state
    handleGetStockItemsDepartmentsInfo();

    // Call function to fetch stock items products information and update 'menuProducts' state
    handleGetStockItemsProductsInfo();

    // Call function to fetch supplier information and update 'supplierDetailsNode' state
    handleGetSupplierInfo();

    // Call function to fetch all stock departments for a specific group and update relevant states
    getAllStockDepartmentForGroupNotLoad(groupId);
  };

  /**
   * Transforms the response data into a structured stock order item format.
   * @param {Array} response - The response data containing stock order items. format (item->department->group)
   * @returns {Array} - Transformed stock order item structure. format (group->department->item)
   */
  const handleStockOrderItemStructure = (response: any) => {
    // Initialize an empty array to store the transformed structure
    const transformedStructure = response.reduce(
      (accumulator: any, item: any) => {
        if (item?.stockDepartmentId?.stockGroupId?.id) {
          const stockGroupId = item.stockDepartmentId.stockGroupId.id;
          const existingGroup = accumulator?.find(
            (group: any) => group.id === stockGroupId,
          );

          if (existingGroup) {
            const existingDept = existingGroup.stockDepartmentId.find(
              (dept: any) => dept.id === item.stockDepartmentId.id,
            );

            if (existingDept) {
              existingDept.item.push({
                id: item.id,
                name: item.name,
                barcode: item.barcode,
                active: item.active,
                created: item.created,
                criticalThreshold: item.criticalThreshold,
                displaySuffix: item.displaySuffix,
                inputMultiplier: item.inputMultiplier,
                inputSuffix: item.inputSuffix,
                primaryLocationId: item.primaryLocationId,
                sku: item.sku,
                updated: item.updated,
                version: item.version,
                warningThreshold: item.warningThreshold,
                stockDepartmentId: item.stockDepartmentId.id,
                autoUnitConvert: item.autoUnitConvert,
                showRemainderSep: item.showRemainderSep,
              });
            } else {
              existingGroup.stockDepartmentId.push({
                id: item.stockDepartmentId.id,
                name: item.stockDepartmentId.name,
                locationId: item.stockDepartmentId.locationId,
                created: item.stockDepartmentId.created,
                updated: item.stockDepartmentId.updated,
                version: item.stockDepartmentId.version,
                item: [
                  {
                    id: item.id,
                    name: item.name,
                    barcode: item.barcode,
                    active: item.active,
                    created: item.created,
                    criticalThreshold: item.criticalThreshold,
                    displaySuffix: item.displaySuffix,
                    inputMultiplier: item.inputMultiplier,
                    inputSuffix: item.inputSuffix,
                    primaryLocationId: item.primaryLocationId,
                    sku: item.sku,
                    updated: item.updated,
                    version: item.version,
                    warningThreshold: item.warningThreshold,
                    stockDepartmentId: item.stockDepartmentId.id,
                    autoUnitConvert: item.autoUnitConvert,
                    showRemainderSep: item.showRemainderSep,
                  },
                ],
              });
            }
          } else {
            accumulator.push({
              id: item.stockDepartmentId.stockGroupId.id,
              name: item.stockDepartmentId.stockGroupId.name,
              version: item.stockDepartmentId.stockGroupId.version,
              updated: item.stockDepartmentId.stockGroupId.updated,
              created: item.stockDepartmentId.stockGroupId.created,
              locationId: item.stockDepartmentId.stockGroupId.locationId,
              stockDepartmentId: [
                {
                  id: item.stockDepartmentId.id,
                  name: item.stockDepartmentId.name,
                  locationId: item.stockDepartmentId.locationId,
                  created: item.stockDepartmentId.created,
                  updated: item.stockDepartmentId.updated,
                  version: item.stockDepartmentId.version,
                  item: [
                    {
                      id: item.id,
                      name: item.name,
                      barcode: item.barcode,
                      active: item.active,
                      created: item.created,
                      criticalThreshold: item.criticalThreshold,
                      displaySuffix: item.displaySuffix,
                      inputMultiplier: item.inputMultiplier,
                      inputSuffix: item.inputSuffix,
                      primaryLocationId: item.primaryLocationId,
                      sku: item.sku,
                      updated: item.updated,
                      version: item.version,
                      warningThreshold: item.warningThreshold,
                      stockDepartmentId: item.stockDepartmentId.id,
                      autoUnitConvert: item.autoUnitConvert,
                      showRemainderSep: item.showRemainderSep,
                    },
                  ],
                },
              ],
            });
          }
        }

        return accumulator;
      },
      [],
    );

    // Return the transformed structure
    return transformedStructure;
  };

  /**
   * Fetch All Stock Departments for a Group (Without Loading Data)
   *
   * This function is responsible for fetching all stock departments for a specific group without loading data into
   * various states. It performs API requests to obtain the necessary information.
   *
   * @param {any} groupId - The identifier of the group for which departments are fetched.
   */
  const getAllStockDepartmentForGroupNotLoad = async (groupId: any) => {
    // Clear and initialize relevant states
    setStockItemGroupDepartmentNodeInSelector([]);
    setStockItemGroupDepartmentNodeInSelectorInitial([]);
    setSelectedDepartmentId("");
    try {
      // Attempt to fetch stock items products information using the 'fetchAllStockItemsProductInfo' API
      const res = await fetchStockItemInfoGetAll(match.params.locationId);

      // Check if the API response contains data
      if (res.data.data) {
        // Set the group identifier in state
        setGroupId(groupId);
        // Transform the stock item data into a structured format
        const transformedStructure = handleStockOrderItemStructure(
          res.data.data,
        );

        // Set the transformed data into relevant states
        setStockItemGroupDepartmentNodeInSelector(transformedStructure);
        setStockItemGroupDepartmentNodeInSelectorInitial(transformedStructure);

        if (!_.isEmpty(searchName)) {
          handleChangeSearchUsingResponse(res.data.data);
        }
      }

      // Set loading states to false
      setIsLoading(false);
      setIsOpenSkeletonLoading(false);
    } catch (err) {
      // Set loading states to false in case of an error
      setIsLoading(false);
      setIsOpenSkeletonLoading(false);
      // If an error occurs during the API call, set the 'error' state with an unexpected error message
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  /**
   * This function is responsible for fetching all stock groups, typically used for populating a menu or selector with
   * available groups. It performs an API request to obtain the necessary information.
   *
   * @param {any} newGroupId - Optional: The identifier of a specific group to select.
   * @param {any} isLoad - Optional: Indicates whether to load data or not.
   */
  const getAllStockGroups = async (newGroupId?: any, isLoad?: any) => {
    try {
      // Attempt to fetch stock items products information using the 'fetchAllStockItemsProductInfo' API
      const res = await fetchAllStockGroups(match.params.locationId);

      // Set the 'menuProducts' state with the retrieved data from the response
      if (res.data.data) {
        if (newGroupId) {
          // If a new group identifier is provided, find and set the selected group object
          const dept = res.data.data.filter(
            (data: any) => data.id === newGroupId,
          );
          setSelectedGroupObj(dept[0]);
        } else {
          // If no new group identifier is provided, set the selected group object as the first group in the response
          setSelectedGroupObj(res.data.data[0]);
        }

        // Set the 'stockGroupSelectedNode' state with the retrieved data
        setStockGroupSelectedNode(res.data.data);
      } else {
        // Set loading states to false in case of an error
        setIsLoading(false);
        setIsOpenSkeletonLoading(false);
      }
    } catch (err) {
      // If an error occurs during the API call, set the 'error' state with an unexpected error message
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  /**
   * Function to fetch location information and initialize stock items loading.
   * @param {string} searchName - Name to search for locations.
   */
  const getAllFilterLocation = async (searchName: string) => {
    try {
      // Attempt to fetch location information using the 'fetchAllFilterLocations' API
      const res = await fetchAllFilterLocations(searchName);

      // Initialize an array to hold location data
      let locationList: any = [];

      // Check if location data is not empty in the response
      if (!_.isEmpty(res.data.data)) {
        // Iterate through each location in the response data
        res.data.data.forEach((locationData: Location) => {
          // Push an object with correct property name and label properties to the locationList array
          locationList.push({
            id: locationData.id, // Use the correct property name 'locationId'
            label: locationData.businessDisplayName,
          });
        });
      }
      // Update the 'locationSelectorList' state with the fetched location list
      setLocationSelectorList(locationList);

      // Set the 'isGetLocationInfo' state to true to indicate location data has been fetched
      setIsGetLocationInfo(true);

      // Trigger the function to fetch stock items information with appropriate parameters
      handleGetProductDepartmentGroup("", "");
      getAllStockGroups();
    } catch (error) {
      // If an error occurs during the API call, set the 'error' state
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  /**
   * Function to handle sorting of stock items list.
   * @param {any} headerName - The selected header name for sorting.
   * @param {any} sortingMethod - The selected sorting method.
   */
  const handleChangeStockItemsListSorting = (
    headerName: any,
    sortingMethod: any,
  ) => {
    // Set the 'disableButton' state to true to disable the sorting button temporarily
    setDisableButton(true);

    // Fetch stock items information with the updated sorting parameters
    getAllStockDepartmentForGroupNotLoad(groupId);
  };

  /**
   * Function to remove sorting from the stock items list.
   */
  const handleGetStockItemDetails = () => {
    // Set the 'disableButton' state to false to enable the sorting button
    setDisableButton(false);

    // Fetch stock items information without sorting
    getAllStockDepartmentForGroupNotLoad(groupId);
  };

  /**
   * Function to handle opening the create stock items modal.
   */
  const handleOpenCreateStockItemsModal = () => {
    // Reset the 'id' state to empty string
    setId("");
    setCreatedId([]);
    setSelectedGroupObj({});
    // Reset the 'selectedNodeData' state to an empty array
    setSelectedNodeData([]);
    setIsGetApi(true);

    // Close the 'isOpenMapProductsEditModal' if it was open
    setIsOpenMapProductsEditModal(false);

    // Close the 'isOpenEditStockItemsModal' if it was open
    setIsOpenEditStockItemsModal(false);

    // Open the 'isOpenCreateStockItemsModal'
    setIsOpenCreateStockItemsModal(true);
  };

  /**
   * This function is responsible for fetching details of stock mapping for a given stock item.
   *
   * @param {string} stockId - The identifier of the stock item for which mapping details are to be fetched.
   */
  const handleGetStockMappingDetails = async (stockId: string) => {
    try {
      // Attempt to fetch stock mapping details using the 'fetchMapProductInfo' API
      const res = await fetchMapProductInfo(match.params.locationId, stockId);

      // Check if the response status is 404 (Not Found)
      if (res.data.status === 404) {
        // If the stock mapping is not found, set an empty array for mapping details
        setStockMappingDetailsNode([]);
      } else {
        // If mapping details are found, set them in the 'stockMappingDetailsNode' state
        setStockMappingDetailsNode(res.data.data);
      }

      // Disable the loading indicator for mapping details
      setMapProductSkeletonLoading(false);
    } catch (error) {
      // If an error occurs during the API call, set the 'error' state with an unexpected error message
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);

      // Set an empty array for mapping details
      setStockMappingDetailsNode([]);

      // Disable the loading indicator for mapping details
      setMapProductSkeletonLoading(false);
    }
  };

  /**
   * This function is responsible for fetching information about suppliers.
   */
  const handleGetSupplierInfo = async () => {
    try {
      // Attempt to fetch supplier information using the 'fetchAllSuppliersInfo' API
      const res = await fetchAllSuppliersInfo(
        match.params.locationId,
        5000,
        1,
        "",
        "",
      );

      // Check if the response status is 404 (Not Found)
      if (res.data.status === 404) {
        // If no supplier information is found, set an empty array for supplier details
        setSupplierDetailsNode([]);
        setSupplierDetailsNodeInitial([]);
      } else {
        // If supplier information is found, set it in the 'supplierDetailsNode' state
        setSupplierDetailsNode(res.data.data);
        const newSuppliers: any = {};
        res.data.data.forEach((supplier: any) => {
          newSuppliers[supplier.id] = { id: supplier.id, name: supplier.name };
        });
        setSupplierDetailsNodeObj(newSuppliers);
        setSupplierDetailsNodeInitial(res.data.data);
      }

      // Disable the loading indicator for supplier information
      setMapProductSkeletonLoading(false);
    } catch (error) {
      // If an error occurs during the API call, set the 'error' state with an unexpected error message

      // Set an empty array for supplier details
      setSupplierDetailsNode([]);
      setSupplierDetailsNodeInitial([]);

      // Disable the loading indicator for supplier information
      setMapProductSkeletonLoading(false);

      // Set the 'error' state with an unexpected error message
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  /**
   * Function to handle opening the edit stock items modal.
   * @param {string} stockId - The ID of the selected stock item.
   * @param {React.MouseEvent<HTMLLIElement>} event - The event object for the click (optional).
   */
  const handleOpenEditStockItemsModal = (
    nodeData: any,
    event: React.MouseEvent<HTMLLIElement>,
  ) => {
    // Check if an event object is provided and stop its propagation to prevent unintended side effects.
    setCreatedId([]);
    handleGetSelectedSupplier();
    setIsGetApi(true);
    // Reset the 'id' state to an empty string.
    setId(nodeData.id);
    // Set the 'selectedNodeData' state to the provided 'stockId'.
    setSelectedNodeData(nodeData);

    // Close the 'isOpenMapProductsEditModal' if it was open.
    setIsOpenMapProductsEditModal(false);

    // Close the 'isOpenCreateStockItemsModal' if it was open.
    setIsOpenCreateStockItemsModal(true);

    // Open the 'isOpenEditStockItemsModal'.
    setIsOpenEditStockItemsModal(true);
  };

  const [displaySuffix, setDisplaySuffix] = useState("");

  /**
   * Function to handle opening the edit map products items modal.
   * @param {any} nodeData - The ID of the selected stock item.
   * @param {React.MouseEvent<HTMLLIElement>} event - The event object for the click (optional).
   */
  const handleOpenEditMapProductsItemsModal = (
    nodeData: any,
    event?: React.MouseEvent<HTMLLIElement>,
  ) => {
    // Check if an event object is provided and stop its propagation to prevent unintended side effects.
    if (event) {
      event.stopPropagation();
    }
    setIsGetApi(false);
    setDisplaySuffix(nodeData.displaySuffix);
    setCreatedId([]);
    setMapProductSkeletonLoading(true);
    handleGetStockMappingDetails(nodeData.id);

    // Reset the 'selectedNodeData' state to an empty array.
    setSelectedNodeData({});

    // Close the 'isOpenEditStockItemsModal' if it was open.
    setIsOpenEditStockItemsModal(false);

    // Set the 'id' state to the provided 'stockId'.
    setId(nodeData.id);

    // Close the 'isOpenCreateStockItemsModal' if it was open.
    setIsOpenCreateStockItemsModal(true);

    // Open the 'isOpenMapProductsEditModal'.
    setIsOpenMapProductsEditModal(true);
  };

  const [isEditLoading, setIsEditLoading] = useState(true);

  /**
   * This function is responsible for fetching information about selected suppliers.
   */
  const handleGetSelectedSupplier = async () => {
    try {
      // Attempt to fetch selected supplier information using the 'fetchAllSelectedSuppliersInfo' API
      const res = await fetchAllSelectedSuppliersInfo(match.params.locationId);

      // Check if the response contains data
      if (res.data.data) {
        // If selected supplier information is found, set it in the 'initialSelectedSupplierList' state
        setInitialSelectedSupplierList(res.data.data);
      } else {
        // If no selected supplier information is found, set an empty array for 'initialSelectedSupplierList'
        setInitialSelectedSupplierList([]);
      }

      setIsEditLoading(false);
    } catch (error) {
      // If an error occurs during the API call, set the 'error' state with an unexpected error message

      // Set an empty array for supplier details
      setSupplierDetailsNode([]);
      setSupplierDetailsNodeInitial([]);

      // Disable the loading indicator for supplier information
      setMapProductSkeletonLoading(false);

      // Set the 'error' state with an unexpected error message
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);

      setIsEditLoading(false);
    }
  };

  /**
   * This function is responsible for handling the opening of the supplier edit modal
   * for a specific stock item.
   *
   * @param {string} stockId - The ID of the stock item.
   * @param {React.MouseEvent<HTMLLIElement>} event - The mouse click event (optional).
   */
  const handleOpenEdtSupplierToTheStockItems = (
    stockId: string,
    event: React.MouseEvent<HTMLLIElement>,
  ) => {
    // Check if an event object is provided and stop its propagation to prevent unintended side effects.
    if (event) {
      event.stopPropagation();
    }

    // Fetch information about selected suppliers
    handleGetSelectedSupplier();

    // Set the 'isCreated' state to 'true'
    setIsCreated(true);

    // Close various modals if they are open
    setIsOpenEditStockItemsModal(false);
    setIsOpenMapProductsEditModal(false);
    setIsOpenCreateStockItemsModal(true);

    // Set the 'id' state to the provided 'stockId'.
    setId(stockId);
  };

  /**
   * Handles the search functionality for filtering stock item groups, departments, and items.
   *
   * @param {any} response - The search query entered by the user.
   */
  const handleChangeSearchUsingResponse = (response: any) => {
    // Filter and update the data based on the search response
    const filteredOutput = response
      .map((group: any) => ({
        ...group,
        stockDepartmentId: group.stockDepartmentId
          .map((dept: any) => ({
            ...dept,
            // Filter items within each department by name
            item: dept.item.filter((item: any) =>
              item.name.includes(searchName),
            ),
          }))
          // Remove departments with no matching items
          .filter((dept: any) => dept.item.length > 0),
      }))
      // Remove groups with no matching departments
      .filter((group: any) => group.stockDepartmentId.length > 0);

    // Update the state with the filtered data
    setStockItemGroupDepartmentNodeInSelector(filteredOutput);
  };

  /**
   * Handles the search functionality for filtering stock item groups, departments, and items.
   *
   * @param {string} value - The search query entered by the user.
   */
  const handleChangeSearch = (value: string) => {
    // Create a deep clone of the initial data to work with
    const cloneStockItemGroupDepartmentNodeInSelector = _.cloneDeep(
      stockItemGroupDepartmentNodeInSelectorInitial,
    );

    // Update the search input value in the state
    setSearchNameTab(value);
    const ids: any = [];
    // Check if the search value is not empty
    if (!_.isEmpty(value)) {
      // Filter and update the data based on the search value
      const filteredOutput = cloneStockItemGroupDepartmentNodeInSelector
        .map((group: any) => ({
          ...group,
          stockDepartmentId: group.stockDepartmentId
            .map((dept: any) => ({
              ...dept,
              // Filter items within each department by name
              item: dept.item.filter((item: any) =>
                item.name
                  .toString()
                  .toLowerCase()
                  .includes(value.toString().toLowerCase()),
              ),
            }))
            .filter((dept: any) => {
              if (!ids.includes(dept.id) && dept.item.length > 0) {
                ids.push(dept.id);
              }
              return dept.item.length > 0;
            }), // Remove departments with no items
        }))
        // Remove groups with no matching departments
        .filter((group: any) => group.stockDepartmentId.length > 0);
      setSearchedDepartmentList(ids);
      // Update the state with the filtered data
      setStockItemGroupDepartmentNodeInSelector(filteredOutput);
    } else {
      // If the search value is empty, reset the data to the initial state
      setStockItemGroupDepartmentNodeInSelector(
        stockItemGroupDepartmentNodeInSelectorInitial,
      );
      setSearchedDepartmentList([]);
    }
  };

  /**
   * This function registers an element with a specific title in a dictionary.
   *
   * @param {any} title - The title to associate with the element.
   * @param {any} elementRef - The reference to the element being registered.
   */
  const registerElementWithTitle = (title: any, elementRef: any) => {
    // Check if both 'title' and 'elementRef' are not empty.
    if (!_.isEmpty(title) && !_.isEmpty(elementRef)) {
      // Store the element reference in the 'elementsByTitle' dictionary with the provided 'title' as the key.
      elementsByTitle[title] = elementRef;
    }
  };

  const handleChangeSearchSupplier = (value: any) => {
    setSearchName(value);
    if (value) {
      setIsSearch(true);
      if (!_.isEmpty(supplierDetailsNodeInitial)) {
        const filterSupplierList = supplierDetailsNodeInitial.filter(
          (supplier: any) =>
            supplier.name.toLowerCase().includes(value.toLowerCase()),
        );
        setSupplierDetailsNode(filterSupplierList);
      }
    } else {
      setIsSearch(false);
      setSupplierDetailsNode(supplierDetailsNodeInitial);
    }
  };

  const handleClearSearchSupplier = () => {
    setSupplierDetailsNode(supplierDetailsNodeInitial);
    setIsSearch(false);
    setSearchName("");
  };

  const handleOpenWizardModal = () => {
    setIsOpenWizard(true);
  };

  return (
    <>
      <Toast message={toastMessage} type={toastType} loading={isLoadingToast} />
      {/* Main container */}
      {/* InventoryDetails component */}

      {isOpenCreateStockItemsModal && (
        <AddStockItemsModal
          isOpenCreateStockItemsModal={isOpenCreateStockItemsModal}
          setIsOpenCreateStockItemsModal={setIsOpenCreateStockItemsModal}
          setError={setError}
          setIsCreated={setIsCreated}
          isCreated={isCreated}
          handleGetStockItemDetails={handleGetStockItemDetails}
          isOpenEditStockItemsModal={isOpenEditStockItemsModal}
          selectedNodeData={selectedNodeData}
          id={id}
          setStockMappingDetailsNode={setStockMappingDetailsNode}
          handleOpenEditMapProductsItemsModal={
            handleOpenEditMapProductsItemsModal
          }
          setSelectedNodeData={setSelectedNodeData}
          supplierDetailsNode={supplierDetailsNode}
          initialSelectedSupplierList={initialSelectedSupplierList}
          getAllStockDepartmentForGroupNotLoad={
            getAllStockDepartmentForGroupNotLoad
          }
          setGroupId={setGroupId}
          getAllStockGroup={getAllStockGroups}
          selectedGroupObj={selectedGroupObj}
          setSelectedGroupObj={setSelectedGroupObj}
          stockGroupSelectedNode={stockGroupSelectedNode}
          selectedDepartmentIdInMenu={selectedDepartmentId}
          handleGetSupplierInfo={handleGetSupplierInfo}
          handleGetSelectedSupplier={handleGetSelectedSupplier}
          setCreatedId={setCreatedId}
          isLoadingToast={isLoadingToast}
          setIsLoadingToast={setIsLoadingToast}
          setToastType={setToastType}
          setToastMessage={setToastMessage}
          handleChangeSearchSupplier={handleChangeSearchSupplier}
          handleClearSearchSupplier={handleClearSearchSupplier}
          searchName={searchName}
          isSearch={isSearch}
          setSearchName={setSearchName}
          setSearchNameTab={setSearchNameTab}
          setIsSearch={setIsSearch}
          displaySuffix={displaySuffix}
          isGetApi={isGetApi}
          isOpenNewModal={isOpenNewModal}
          setIsOpenNewModal={setIsOpenNewModal}
          setDisplaySuffix={setDisplaySuffix}
          setIsOpenMapProductsEditModal={setIsOpenMapProductsEditModal}
        />
      )}

      {isOpenMapProductsEditModal && (
        <AddMapProductModal
          isOpenCreateStockItemsModal={isOpenCreateStockItemsModal}
          setIsOpenCreateStockItemsModal={setIsOpenCreateStockItemsModal}
          setError={setError}
          setIsCreated={setIsCreated}
          menuGroups={menuGroups}
          menuDepartments={menuDepartments}
          menuProducts={menuProducts}
          isOpenMapProductsEditModal={isOpenMapProductsEditModal}
          id={id}
          stockMappingDetailsNode={stockMappingDetailsNode}
          mapProductSkeletonLoading={mapProductSkeletonLoading}
          setStockMappingDetailsNode={setStockMappingDetailsNode}
          isLoadingToast={isLoadingToast}
          setIsLoadingToast={setIsLoadingToast}
          setToastType={setToastType}
          setToastMessage={setToastMessage}
          displaySuffix={displaySuffix}
          setMapProductSkeletonLoading={setMapProductSkeletonLoading}
        />
      )}

      <InventoryDetails
        locationSelectorList={locationSelectorList}
        isOpenSkeletonLoading={isOpenSkeletonLoading}
        isGetLocationInfo={isGetLocationInfo}
        topic="Stock Items"
        handleOpenCreateModal={handleOpenCreateStockItemsModal}
        buttonName="Add Stock Item"
        handleChangeSearch={handleChangeSearch}
        searchNameTab={searchNameTab}
        setSearchNameTab={setSearchNameTab}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        activeGroupId={activeGroupId}
        setActiveGroupId={setActiveGroupId}
        selectedDepartmentId={selectedDepartmentId}
        stockItemGroupDepartmentNodeInSelector={
          stockItemGroupDepartmentNodeInSelector
        }
        isOpenDesktopModal={props.additionalProp}
        setSelectedDepartmentId={setSelectedDepartmentId}
        stockItemGroupDepartmentNodeInSelectorInitial={
          stockItemGroupDepartmentNodeInSelectorInitial
        }
        createdId={createdId}
        setCreatedId={setCreatedId}
        searchedDepartmentList={searchedDepartmentList}
        isActiveLocationId={isActiveLocationId}
        isWizard={true}
        handleOpenWizardModal={handleOpenWizardModal}
      >
        {/* Render AddStockItemsModal if isOpenCreateStockItemsModal is true */}

        {/* StockItems table component */}
        <Grid container>
          <Grid item xs={12} style={{ display: "flex", marginTop: "16px" }}>
            <div style={{ display: "block", width: "100%" }}>
              <StockItemsHeader
                handleChangeStockItemsListSorting={
                  handleChangeStockItemsListSorting
                }
                disableButton={disableButton}
                isLoading={isLoading}
                locationSelectorList={locationSelectorList}
                handleOpenEditStockItemsModal={handleOpenEditStockItemsModal}
                handleOpenEditMapProductsItemsModal={
                  handleOpenEditMapProductsItemsModal
                }
                handleOpenEdtSupplierToTheStockItems={
                  handleOpenEdtSupplierToTheStockItems
                }
                stockItemGroupDepartmentNodeInSelector={
                  stockItemGroupDepartmentNodeInSelector
                }
                selectedDepartmentId={selectedDepartmentId}
                searchName={searchName}
                registerElementWithTitle={registerElementWithTitle}
                stockItemGroupDepartmentNodeInSelectorInitial={
                  stockItemGroupDepartmentNodeInSelectorInitial
                }
                activeGroupId={activeGroupId}
                searchNameTab={searchNameTab}
              />
            </div>
          </Grid>
        </Grid>
      </InventoryDetails>
      {isOpenWizard && (
        <WizardModal
          isOpenWizard={isOpenWizard}
          setIsOpenWizard={setIsOpenWizard}
          setSearchName={setSearchName}
          setSearchNameTab={setSearchNameTab}
          setIsSearch={setIsSearch}
          getAllStockGroup={getAllStockGroups}
          handleGetSupplierInfo={handleGetSupplierInfo}
          stockGroupSelectedNode={stockGroupSelectedNode}
          setStockGroupSelectedNode={setStockGroupSelectedNode}
          selectedGroupObj={selectedGroupObj}
          setSelectedGroupObj={setSelectedGroupObj}
          supplierDetailsNode={supplierDetailsNode}
          id={id}
          handleChangeSearchSupplier={handleChangeSearchSupplier}
          handleClearSearchSupplier={handleClearSearchSupplier}
          searchName={searchName}
          isSearch={isSearch}
          handleGetSelectedSupplier={handleGetSelectedSupplier}
          getAllStockDepartmentForGroupNotLoad={
            getAllStockDepartmentForGroupNotLoad
          }
          handleOpenEditMapProductsItemsModal={
            handleOpenEditMapProductsItemsModal
          }
          supplierDetailsNodeObj={supplierDetailsNodeObj}
        />
      )}

      {/* DefaultAlert components for success and error messages */}
      <DefaultAlert
        open={!!success}
        handleClose={() => setSuccess("")}
        message={success}
        severity={"success"}
      />
      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </>
  );
};

export default withAuthority(StockItems, Authorities.INVENTORY_ADMIN_READ);
